<template>
  <div class="trade-tree">
    <div class="font-sans border border-solid border-gray-400" :id="'container-' + _uid"></div>
    <div class="trade-tree-legend flex flex-wrap items-center justify-start mt-3 text-xs text-gray-700">
      <span class="flex items-center mr-6 mb-1">
        <span
          class="inline-block w-4 h-4 mr-2 bg-yellow-400 border-2 border-yellow-800 flex-shrink-0"
          aria-hidden="true"
        ></span>
        <span>Currently in Rays organization.</span>
      </span>
      <span class="flex items-center mr-6 mb-1">
        <span class="inline-flex items-end h-4 mr-2" aria-hidden="true">
          <span class="inline-block w-2 h-2 bg-blue-100 border border-gray-700"></span>
          <span class="inline-block w-3 h-3 ml-1 bg-blue-200 border border-gray-700"></span>
          <span class="inline-block w-4 h-4 ml-1 bg-blue-400 border border-gray-700"></span>
        </span>
        <span>Square area = Rays WAR.</span>
      </span>
    </div>
  </div>
</template>

<script>
import * as d3 from 'd3';
import tailwindConfig from '../mixins/tailwindConfig';

export default {
  mixins: [tailwindConfig],

  props: {
    chartData: {
      type: Object,
      required: true,
    },
    playerWar: {
      type: Object,
      default: () => ({ players: {} }),
    },
    playerTrades: {
      type: Object,
      default: () => ({ trades: [] }),
    },
  },

  data() {
    return {
      delmonImage: null,
      root: null,
      chart: null,
      drawnLinks: null,
      drawnNodes: null,
      drawnLabels: null,
      simulation: null,
      tooltip: null,
      width: 800,
      margin: 30,
      height: 740,
      isCompact: false,
      resizeHandler: null,
      resizeTimer: null,
      headAnimationTimer: null,
      prefersReducedMotion: false,
    };
  },

  computed: {
    chartId() {
      return `chart${this._uid}`;
    },

    gridId() {
      return `grid${this._uid}`;
    },

    nodeSizeScale() {
      return d3.scaleSqrt()
        .domain([0, 12])
        .range([18, 48])
        .clamp(true);
    },

    canvas() {
      return document.querySelector('canvas');
    },

    nodes() {
      return this.root.descendants();
    },

    links() {
      return this.root.links();
    },

    dimensions() {
      return {
        width: this.width,
        height: this.height,
        margin: this.margin,
        radius: this.width / 2,
      };
    },
  },

  created() {
    this.root = d3.hierarchy(this.chartData);
  },

  mounted() {
    if (this.chartData) {
      this.prefersReducedMotion = window.matchMedia
        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.configureDimensions();
      this.drawForce();
      this.resizeHandler = () => this.queueResize();
      window.addEventListener('resize', this.resizeHandler);
    }
  },

  beforeDestroy() {
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }

    if (this.resizeTimer) {
      window.clearTimeout(this.resizeTimer);
    }

    this.clearHeadAnimationTimer();

    if (this.simulation) {
      this.simulation.stop();
    }
  },

  methods: {
    drawForce() {
      this.chart = this.createChart();
      this.buildSimulation();
      this.drawLinks();
      this.drawNodes();
      this.drawSquares();
      this.insertImage();
      this.drawText();
      this.drawTooltip();
      this.onTick();
      this.scheduleHeadAnimation(1200);
    },

    configureDimensions() {
      const container = document.getElementById(`container-${this._uid}`);

      if (!container) {
        return;
      }

      const containerWidth = Math.floor(container.getBoundingClientRect().width);

      this.isCompact = containerWidth < 640;
      this.width = this.isCompact ? Math.max(280, containerWidth) : 800;
      this.height = this.isCompact ? 820 : 740;
    },

    queueResize() {
      if (this.resizeTimer) {
        window.clearTimeout(this.resizeTimer);
      }

      this.resizeTimer = window.setTimeout(() => {
        const previousWidth = this.width;
        const previousHeight = this.height;

        this.configureDimensions();

        if (Math.abs(previousWidth - this.width) > 8 || previousHeight !== this.height) {
          this.restart();
        }
      }, 150);
    },

    buildSimulation() {
      this.simulation = d3.forceSimulation(this.root.descendants())
        .force('charge', d3.forceManyBody().strength(this.isCompact ? -340 : -600))
        .force('link', d3.forceLink(this.root.links()).distance(this.isCompact ? 88 : 100))
        .force('collision', d3.forceCollide()
          .radius(d => d.depth === 0 ? 72 : Math.max(
            this.nodeSize(d.data) / 2 + (this.isCompact ? 16 : 22),
            this.labelHalfWidth(d) + 8
          ))
          .strength(0.85))
        .force('center', d3.forceCenter().x(this.dimensions.width / 2).y(this.dimensions.height / 2));
    },

    onTick() {
      this.simulation.on('tick', () => {
        this.nodes[0].x = this.dimensions.width / 2;
        this.nodes[0].y = this.dimensions.height / 2;

        this.drawnNodes.each((d) => {
          const halfSize = this.nodeHalfSize(d);
          const horizontalMargin = Math.max(halfSize + 10, this.labelHalfWidth(d) + 6);
          const topMargin = halfSize + 30;
          const bottomMargin = halfSize + 10;

          d.x = Math.max(horizontalMargin, Math.min(this.dimensions.width - horizontalMargin, d.x));
          d.y = Math.max(topMargin, Math.min(this.dimensions.height - bottomMargin, d.y));
        });

        this.drawnLinks.attr('d', d => this.linkPath(d));

        this.drawnNodes.attr('transform', d => `translate(${d.x },${d.y })`);
      });
    },

    drawLinks() {
      this.drawnLinks = this.chart.selectAll('.trade-link')
          .data(this.links)
          .enter()
          .append('path')
          .attr('class', 'trade-link')
          .attr('fill', 'none')
          .attr('stroke', '#B7C1CB')
          .attr('stroke-width', 1.25)
          .attr('stroke-linecap', 'square')
          .attr('stroke-linejoin', 'miter')
          .attr('vector-effect', 'non-scaling-stroke');
    },

    drawTooltip() {
      this.tooltip = d3.select('#container-' + this._uid)
        .append('div')
        .attr('class', 'tooltip border')
        .style('opacity', 0);
    },

    drawText() {
      this.drawnLabels = this.drawnNodes
        .append('text')
        .attr('class', 'player-label')
        .attr('x', 0)
        .attr('y', d => -(this.nodeHalfSize(d) + 9))
        .attr('text-anchor', 'middle')
        .attr('fill', '#23313D')
        .attr('stroke', '#F8F7F2')
        .attr('stroke-width', 3)
        .attr('stroke-linejoin', 'round')
        .style('paint-order', 'stroke')
        .style('font-size', this.isCompact ? '10px' : '11px')
        .style('font-weight', 500)
        .text((d, i) => !i ? null : d.data.name);
    },


    drawNodes() {
      this.drawnNodes = this.chart.selectAll('g')
        .data(this.nodes)
        .enter()
        .append('g')
        .call(d3.drag()  //Define what to do on drag events
          .on('start', d => this.dragStarted(d))
          .on('drag', d => this.dragging(d))
          .on('end', d => this.dragEnded(d)));
    },

    drawSquares() {
      this.drawnNodes.append('rect')
        .attr('tabindex', d => d.depth === 0 ? null : 0)
        .attr('role', d => d.depth === 0 ? null : 'button')
        .attr('aria-label', d => d.depth === 0
          ? null
          : `${d.data.name}: view Rays WAR and trade history`)
        .attr('x', d => -this.nodeHalfSize(d))
        .attr('y', d => -this.nodeHalfSize(d))
        .attr('width', d => this.nodeHalfSize(d) * 2)
        .attr('height', d => this.nodeHalfSize(d) * 2)
        .attr('fill', d => d.depth === 0 ? '#F8F7F2' : this.getColorFromRange(Boolean(d.data.active), this.nodeWar(d.data)))
        .attr('stroke', d => d.data.active ? this.twColors.yellow['800'] : '#263746')
        .attr('stroke-width', d => d.data.active ? 2 : 1.5)
        .attr('vector-effect', 'non-scaling-stroke')
        .attr('shape-rendering', 'crispEdges')
        .on('mouseover', d => d.depth === 0
          ? null
          : this.showTooltip(d, d3.event.currentTarget))
        .on('mouseout', () => this.hideTooltip())
        .on('focus', d => d.depth === 0
          ? null
          : this.showTooltip(d, d3.event.currentTarget))
        .on('blur', () => this.hideTooltip())
        .on('touchstart.tooltip', d => {
          if (d.depth === 0) {
            return;
          }

          d3.event.stopPropagation();
          this.showTooltip(d, d3.event.currentTarget);
        });
    },

    showTooltip(player, anchorNode) {
      this.tooltip
        .interrupt()
        .html(this.tooltipHtml(player))
        .style('opacity', 0);

      const tooltipBounds = this.tooltip.node().getBoundingClientRect();
      const viewportLeft = window.pageXOffset;
      const viewportTop = window.pageYOffset;
      const viewportRight = viewportLeft + window.innerWidth;
      const viewportBottom = viewportTop + window.innerHeight;
      const cursorGap = 14;
      const anchor = this.tooltipAnchor(anchorNode);
      let left = anchor.x + cursorGap;
      let top = anchor.y - tooltipBounds.height / 2;

      if (left + tooltipBounds.width > viewportRight - 8) {
        left = anchor.x - tooltipBounds.width - cursorGap;
      }

      left = Math.max(viewportLeft + 8, left);
      top = Math.max(
        viewportTop + 8,
        Math.min(viewportBottom - tooltipBounds.height - 8, top)
      );

      this.tooltip
        .style('left', `${left}px`)
        .style('top', `${top}px`)
        .transition()
          .duration(120)
          .style('opacity', 1);
    },

    tooltipAnchor(anchorNode) {
      const event = d3.event;
      const touch = event && (
        (event.touches && event.touches[0])
        || (event.changedTouches && event.changedTouches[0])
      );
      const pointer = touch || event;

      if (
        pointer
        && Number.isFinite(pointer.pageX)
        && Number.isFinite(pointer.pageY)
      ) {
        return {
          x: pointer.pageX,
          y: pointer.pageY,
        };
      }

      const bounds = anchorNode.getBoundingClientRect();

      return {
        x: bounds.left + bounds.width / 2 + window.pageXOffset,
        y: bounds.top + bounds.height / 2 + window.pageYOffset,
      };
    },

    hideTooltip() {
      this.tooltip
        .interrupt()
        .transition()
        .duration(100)
        .style('opacity', 0);
    },

    insertImage() {
      this.delmonImage = this.drawnNodes
        .filter(d => d.depth === 0)
        .append('svg:image')
        .attr('xlink:href', d => d.data.img)
        .attr('x', -54)
        .attr('y', -54)
        .attr('height', 108)
        .attr('width', 108)
        .attr('class', 'delmon-head shadow');

      if (!this.prefersReducedMotion) {
        this.delmonImage
          .attr('role', 'button')
          .attr('tabindex', 0)
          .attr('aria-label', 'Animate Delmon Young headshot')
          .on('mouseenter', () => this.nudgeHead())
          .on('click', () => this.nudgeHead())
          .on('keydown', () => {
            if (d3.event.key === 'Enter' || d3.event.key === ' ') {
              d3.event.preventDefault();
              this.nudgeHead();
            }
          });
      }
    },

    nudgeHead() {
      this.animateHead();
      this.scheduleHeadAnimation();
    },

    animateHead() {
      if (this.prefersReducedMotion || !this.delmonImage) {
        return;
      }

      this.delmonImage
        .interrupt('head-wiggle')
        .attr('transform', 'translate(0 0) rotate(0)')
        .transition('head-wiggle')
          .duration(120)
          .ease(d3.easeCubicOut)
          .attr('transform', 'translate(-3 -2) rotate(-7)')
        .transition()
          .duration(110)
          .ease(d3.easeCubicInOut)
          .attr('transform', 'translate(4 1) rotate(8)')
        .transition()
          .duration(100)
          .ease(d3.easeCubicInOut)
          .attr('transform', 'translate(-2 -1) rotate(-4)')
        .transition()
          .duration(180)
          .ease(d3.easeBackOut)
          .attr('transform', 'translate(0 0) rotate(0)');
    },

    scheduleHeadAnimation(delay = 10000 + Math.random() * 6000) {
      this.clearHeadAnimationTimer();

      if (this.prefersReducedMotion) {
        return;
      }

      this.headAnimationTimer = window.setTimeout(() => {
        this.animateHead();
        this.scheduleHeadAnimation();
      }, delay);
    },

    clearHeadAnimationTimer() {
      if (this.headAnimationTimer) {
        window.clearTimeout(this.headAnimationTimer);
        this.headAnimationTimer = null;
      }
    },

    createChart() {
      const chart = d3.select(`#container-${this._uid}`)
        .append('svg')
          .attr('id', this.chartId)
          .attr('viewBox', `0 0 ${this.dimensions.width} ${this.dimensions.height}`)
          .attr('preserveAspectRatio', 'xMidYMid meet')
          .style('display', 'block')
          .style('width', '100%')
          .style('height', 'auto');

      const grid = chart.append('defs')
        .append('pattern')
          .attr('id', this.gridId)
          .attr('width', 24)
          .attr('height', 24)
          .attr('patternUnits', 'userSpaceOnUse');

      grid.append('path')
        .attr('d', 'M 24 0 L 0 0 0 24')
        .attr('fill', 'none')
        .attr('stroke', '#D9DEE5')
        .attr('stroke-width', 0.75)
        .attr('opacity', 0.42)
        .attr('vector-effect', 'non-scaling-stroke');

      chart.append('rect')
        .attr('width', this.dimensions.width)
        .attr('height', this.dimensions.height)
        .attr('fill', `url(#${this.gridId})`);

      chart.on('touchstart.tooltip', () => this.hideTooltip());

      return chart;
    },

    tooltipHtml(player) {
      const seasons = this.tooltipSeasons(player);
      const trades = this.tooltipTrades(player);
      const rows = seasons.length
        ? seasons.map(season => `
          <tr>
            <td>${season.year}</td>
            <td>${this.formatTooltipWar(season.war)}</td>
          </tr>
        `).join('')
        : `
          <tr>
            <td>${this.tooltipYear(player)}</td>
            <td>-</td>
          </tr>
        `;
      const tradeHistory = trades.length
        ? `
          <div class="tooltip-trades">
            <p class="tooltip-section-title">Trade to Rays</p>
            ${trades.map(trade => `
              <div class="tooltip-trade">
                <p class="tooltip-trade-date">${this.formatTradeDate(trade.date)}</p>
                <dl>
                  <div>
                    <dt>Received</dt>
                    <dd>${this.formatTradePlayers(trade.raysReceived)}</dd>
                  </div>
                  <div>
                    <dt>Sent</dt>
                    <dd>${this.formatTradePlayers(trade.raysSent)}</dd>
                  </div>
                </dl>
              </div>
            `).join('')}
          </div>
        `
        : '';

      return `
        <p class="tooltip-player">${this.escapeHtml(player.data.name)}</p>
        <div class="tooltip-war">
          <p class="tooltip-section-title">WAR during Rays tenure</p>
          <table class="tooltip-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>WAR</th>
              </tr>
            </thead>
            <tbody>${rows}</tbody>
          </table>
        </div>
        ${tradeHistory}
      `;
    },

    tooltipSeasons(player) {
      const players = this.playerWar && this.playerWar.players;
      const record = players && players[player.data.name];

      return record ? record.seasons : [];
    },

    tooltipTrades(player) {
      const trades = this.playerTrades && this.playerTrades.trades;

      if (!Array.isArray(trades)) {
        return [];
      }

      return trades.filter(trade => (
        Array.isArray(trade.treePlayers)
        && trade.treePlayers.includes(player.data.name)
      ));
    },

    tooltipYear(player) {
      return this.escapeHtml((player.data.service || '-')
        .replace(/^Minors,\s*/, '')
        .replace(/-/g, '–'));
    },

    formatTooltipWar(value) {
      const war = Number(value);

      if (!Number.isFinite(war)) {
        return '-';
      }

      return `${war > 0 ? '+' : ''}${war.toFixed(1)}`;
    },

    formatTradeDate(value) {
      const date = new Date(`${value}T00:00:00Z`);

      if (Number.isNaN(date.getTime())) {
        return this.escapeHtml(value);
      }

      return new Intl.DateTimeFormat('en-US', {
        day: 'numeric',
        month: 'short',
        timeZone: 'UTC',
        year: 'numeric',
      }).format(date);
    },

    formatTradePlayers(players) {
      if (!Array.isArray(players)) {
        return '-';
      }

      return players.map(player => this.escapeHtml(player)).join(', ');
    },

    escapeHtml(value) {
      return String(value)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    },

    //Define drag event functions
    dragStarted(d) {
      if (!d3.event.active) {
        this.simulation.alphaTarget(0.3).restart();
      }
      d.fx = d.x;
      d.fy = d.y;
    },

    dragging(d) {
      if (d3.event.x < 16) {
        d.fx = 16;
      } else if (d3.event.x > this.dimensions.width - 16) {
        d.fx = this.dimensions.width - 16;
      } else {
        d.fx = d3.event.x;
      }

      if (d3.event.y < 16) {
        d.fy = 16;
      } else if (d3.event.y > this.dimensions.height - 16) {
        d.fy = this.dimensions.height - 16;
      } else {
        d.fy = d3.event.y;
      }
    },

    dragEnded(d) {
      if (!d3.event.active) {
        this.simulation.alphaTarget(0);
      }

      d.fx = null;
      d.fy = null;
    },

    getColorFromRange(isActive, value) {
      if (isActive) {
        return this.twColors.yellow['400'];
      }

      const getColor = d3.scaleLinear()
        .domain([0, 6, 12])
        .range(this.colorRange());

      return getColor(value);
    },

    colorRange() {
      return [
        '#EEF5FA',
        '#B8D7E8',
        '#4D93BF',
      ];
    },

    nodeSize(player) {
      return this.nodeSizeScale(Math.max(0, this.nodeWar(player)));
    },

    nodeWar(player) {
      const players = this.playerWar && this.playerWar.players;
      const record = players && players[player.name];

      if (record && Number.isFinite(record.total)) {
        return record.total;
      }

      return Number(player.war) || 0;
    },

    nodeHalfSize(node) {
      return node.depth === 0 ? 58 : this.nodeSize(node.data) / 2;
    },

    labelHalfWidth(node) {
      if (node.depth === 0) {
        return this.nodeHalfSize(node);
      }

      const characterWidth = this.isCompact ? 5.5 : 6;
      const maximumWidth = this.isCompact ? 92 : 110;

      return Math.min(maximumWidth, Math.max(
        this.nodeHalfSize(node),
        node.data.name.length * characterWidth / 2
      ));
    },

    linkPath(link) {
      const dx = link.target.x - link.source.x;
      const dy = link.target.y - link.source.y;

      if (Math.abs(dx) >= Math.abs(dy)) {
        const elbowX = link.source.x + Math.sign(dx) * Math.abs(dy);

        return `M ${link.source.x} ${link.source.y} L ${elbowX} ${link.target.y} L ${link.target.x} ${link.target.y}`;
      }

      const elbowY = link.source.y + Math.sign(dy) * Math.abs(dx);

      return `M ${link.source.x} ${link.source.y} L ${link.target.x} ${elbowY} L ${link.target.x} ${link.target.y}`;
    },

    restart() {
      if (this.simulation) {
        this.simulation.stop();
      }

      this.clearHeadAnimationTimer();

      const container = d3.select(`#container-${this._uid}`);

      container.selectAll('svg').remove();
      container.selectAll('.tooltip').remove();
      this.root = d3.hierarchy(this.chartData);
      this.drawForce();
    }
  },
};
</script>

<style>
  .trade-tree text {
    font-family: 'Roboto Mono';
  }

  .trade-tree .delmon-head {
    cursor: pointer;
  }

  .trade-tree .delmon-head:focus {
    outline: 2px solid #4D93BF;
    outline-offset: 2px;
  }

  .trade-tree div.tooltip {
    position: absolute;
    width: 304px;
    max-width: calc(100vw - 16px);
    max-height: calc(100vh - 16px);
    box-sizing: border-box;
    overflow-y: auto;
    color: #23313D;
    background: #FFFFFF;
    border-color: #263746;
    border-radius: 0;
    box-shadow: 3px 3px 0 rgba(38, 55, 70, 0.16);
    pointer-events: none;
    z-index: 10;
  }

  .trade-tree .tooltip-player {
    padding: 8px 9px;
    border-bottom: 2px solid #263746;
    font-family: 'Roboto';
    font-size: 13px;
    font-weight: 600;
    line-height: 1.25;
  }

  .trade-tree .tooltip-section-title {
    padding: 5px 9px;
    color: #6B7782;
    background: #F8F7F2;
    border-bottom: 1px solid #D9DEE5;
    font-family: 'Roboto Mono';
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.08em;
    line-height: 1.2;
    text-transform: uppercase;
  }

  .trade-tree .tooltip-table {
    width: 100%;
    border-collapse: collapse;
    font-family: 'Roboto Mono';
    font-size: 12px;
    font-variant-numeric: tabular-nums;
  }

  .trade-tree .tooltip-table thead {
    color: #6B7782;
    background: #FFFFFF;
    border-bottom: 1px solid #D9DEE5;
    font-size: 10px;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .trade-tree .tooltip-table th,
  .trade-tree .tooltip-table td {
    padding: 4px 9px;
    text-align: left;
  }

  .trade-tree .tooltip-table th:last-child,
  .trade-tree .tooltip-table td:last-child {
    text-align: right;
  }

  .trade-tree .tooltip-table tbody tr + tr {
    border-top: 1px solid #D9DEE5;
  }

  .trade-tree .tooltip-table td:last-child {
    font-weight: 600;
  }

  .trade-tree .tooltip-trades {
    border-top: 2px solid #263746;
  }

  .trade-tree .tooltip-trade {
    padding: 7px 9px 8px;
  }

  .trade-tree .tooltip-trade + .tooltip-trade {
    border-top: 1px solid #D9DEE5;
  }

  .trade-tree .tooltip-trade-date {
    margin-bottom: 5px;
    font-family: 'Roboto Mono';
    font-size: 11px;
    font-weight: 600;
    line-height: 1.2;
  }

  .trade-tree .tooltip-trade dl,
  .trade-tree .tooltip-trade dd {
    margin: 0;
  }

  .trade-tree .tooltip-trade dl > div {
    display: grid;
    grid-template-columns: 54px minmax(0, 1fr);
    column-gap: 7px;
    align-items: baseline;
  }

  .trade-tree .tooltip-trade dl > div + div {
    margin-top: 4px;
  }

  .trade-tree .tooltip-trade dt {
    color: #6B7782;
    font-family: 'Roboto Mono';
    font-size: 9px;
    letter-spacing: 0.05em;
    text-transform: uppercase;
  }

  .trade-tree .tooltip-trade dd {
    font-family: 'Roboto';
    font-size: 11px;
    line-height: 1.35;
  }
</style>
